import { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { X, Send, Sparkles, ShoppingCart, Handshake } from "lucide-react";
import { toast } from "sonner";

export default function ProductDetail({ product, onClose, onBuy }) {
  const [tab, setTab] = useState("overview"); // overview | negotiate
  const [neg, setNeg] = useState(null); // {session_id, list_price, messages, status, locked_price, negotiation_token}
  const [input, setInput] = useState("");
  const [offer, setOffer] = useState("");
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [neg]);

  const startNeg = async () => {
    setBusy(true);
    try {
      const r = await api.post("/negotiate/start", { product_id: product.id });
      setNeg({ ...r.data, locked_price: null, negotiation_token: null });
      setTab("negotiate");
    } catch {
      toast.error("Couldn't start negotiation");
    }
    setBusy(false);
  };

  const sendNeg = async () => {
    if (!neg || busy) return;
    if (!input.trim() && !offer) return;
    const offerNum = offer ? parseFloat(offer) : null;
    const msg = input.trim() || (offerNum ? `I can do $${offerNum.toFixed(2)}.` : "");
    setBusy(true);
    try {
      const r = await api.post("/negotiate/message", {
        session_id: neg.session_id,
        message: msg,
        offer: offerNum,
      });
      const newMessages = [
        ...(neg.messages || []),
        { role: "user", text: msg, ...(offerNum ? { offer: offerNum } : {}) },
        { role: "assistant", text: r.data.reply, ...(r.data.counter_price ? { counter_price: r.data.counter_price } : {}) },
      ];
      setNeg({
        ...neg,
        messages: newMessages,
        status: r.data.status,
        locked_price: r.data.locked_price,
        negotiation_token: r.data.negotiation_token,
      });
      setInput("");
      setOffer("");
      if (r.data.status === "accepted") {
        toast.success(`Deal locked at $${r.data.locked_price.toFixed(2)} ✦`);
      } else if (r.data.status === "rejected") {
        toast.error("Negotiation closed");
      }
    } catch {
      toast.error("Negotiator unreachable");
    }
    setBusy(false);
  };

  const buyAt = (token) => {
    onBuy(product, token);
  };

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center p-4"
      style={{ background: "rgba(15,23,42,0.45)", backdropFilter: "blur(8px)" }}
      onClick={onClose}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.96, y: 12 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        onClick={(e) => e.stopPropagation()}
        className="glass-heavy rounded-3xl max-w-3xl w-full max-h-[90vh] overflow-hidden grain flex flex-col"
        data-testid="product-detail-modal"
      >
        <div className="flex items-stretch border-b border-slate-200">
          {product.image_url && (
            <img src={product.image_url} alt={product.title} className="w-40 h-40 object-cover" />
          )}
          <div className="flex-1 p-5">
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#F5C542]">{product.category}</div>
            <div className="font-display font-bold text-2xl mt-1 leading-tight">{product.title}</div>
            <div className="text-sm text-[#64748B] mt-1 line-clamp-2">{product.description}</div>
            <div className="mt-3 flex items-baseline gap-3">
              <div className="font-display text-3xl font-bold text-[#00E5FF]">${product.price.toFixed(2)}</div>
              <div className="text-xs text-[#94A3B8]">by {product.seller_name}</div>
            </div>
          </div>
          <button onClick={onClose} className="self-start m-3 w-9 h-9 rounded-full hover:bg-slate-100 flex items-center justify-center"><X size={16} /></button>
        </div>

        <div className="flex items-center gap-2 px-5 pt-3">
          <button onClick={() => setTab("overview")} data-testid="tab-overview" className={`text-xs px-3 py-1.5 rounded-full ${tab === "overview" ? "glass-active text-[#00E5FF]" : "glass text-[#64748B]"}`}>Overview</button>
          <button onClick={() => setTab("negotiate")} data-testid="tab-negotiate" className={`text-xs px-3 py-1.5 rounded-full ${tab === "negotiate" ? "glass-active text-[#00E5FF]" : "glass text-[#64748B]"}`}>Negotiate</button>
        </div>

        <div className="flex-1 overflow-y-auto p-5">
          {tab === "overview" && (
            <div className="space-y-4">
              <p className="text-[#0F172A] leading-relaxed whitespace-pre-wrap">{product.description}</p>
              <div className="glass rounded-2xl p-4 text-sm text-[#64748B]">
                <div className="text-xs uppercase tracking-[0.2em] text-[#00E5FF] mb-2">What you get</div>
                Instant digital delivery · Stripe-secured checkout · NEXUS receipt
              </div>
              <div className="flex gap-2">
                <button data-testid="open-negotiate" onClick={startNeg} disabled={busy} className="nexus-btn-secondary flex items-center gap-2">
                  <Handshake size={14} /> Negotiate with AI
                </button>
                <button data-testid="buy-now" onClick={() => buyAt(null)} className="nexus-btn-primary flex items-center gap-2">
                  <ShoppingCart size={14} /> Buy at ${product.price.toFixed(2)}
                </button>
              </div>
            </div>
          )}
          {tab === "negotiate" && (
            <div className="flex flex-col h-full" style={{ minHeight: 360 }}>
              {!neg ? (
                <div className="flex flex-col items-center justify-center py-10 gap-3 text-center">
                  <Sparkles size={32} className="text-[#00E5FF]" />
                  <div className="font-display font-bold">Haggle with the AI seller</div>
                  <p className="text-sm text-[#64748B] max-w-sm">Make an offer below list. If you land within the seller's range, you'll get a locked price token to checkout with.</p>
                  <button data-testid="start-negotiate" onClick={startNeg} disabled={busy} className="nexus-btn-primary">Begin negotiation</button>
                </div>
              ) : (
                <>
                  <div ref={scrollRef} className="flex-1 overflow-y-auto space-y-2 pr-1 max-h-[320px]" data-testid="negotiation-chat">
                    {(neg.messages || []).map((m, i) => (
                      <div key={`${m.role}-${i}-${(m.text || m.content || "").slice(0, 12)}`} className={`text-sm rounded-2xl px-3 py-2 ${
                        m.role === "user"
                          ? "bg-[#00E5FF]/10 border border-[#00E5FF]/30 ml-6"
                          : "glass mr-6"
                      }`}>
                        {m.text}
                        {m.offer != null && <div className="mt-1 text-xs text-[#F5C542]">Offer: ${Number(m.offer).toFixed(2)}</div>}
                        {m.counter_price != null && <div className="mt-1 text-xs text-[#7B2CBF]">Counter: ${Number(m.counter_price).toFixed(2)}</div>}
                      </div>
                    ))}
                  </div>
                  {neg.status === "accepted" && neg.negotiation_token && (
                    <div className="my-3 glass-active rounded-2xl p-4 text-center">
                      <div className="text-xs uppercase tracking-[0.2em] text-[#00E5FF]">Deal locked</div>
                      <div className="font-display text-2xl font-bold mt-1">${neg.locked_price.toFixed(2)}</div>
                      <button data-testid="buy-locked" onClick={() => buyAt(neg.negotiation_token)} className="mt-3 nexus-btn-primary text-sm flex items-center gap-2 mx-auto">
                        <ShoppingCart size={14} /> Checkout at locked price
                      </button>
                    </div>
                  )}
                  {neg.status === "open" && (
                    <div className="mt-3 space-y-2">
                      <div className="flex gap-2">
                        <input
                          data-testid="neg-offer"
                          type="number"
                          step="0.01"
                          min="0.5"
                          value={offer}
                          onChange={(e) => setOffer(e.target.value)}
                          placeholder="Your offer $"
                          className="nexus-input"
                          style={{ maxWidth: 140 }}
                        />
                        <input
                          data-testid="neg-input"
                          value={input}
                          onChange={(e) => setInput(e.target.value)}
                          onKeyDown={(e) => e.key === "Enter" && sendNeg()}
                          placeholder="Add a message…"
                          className="nexus-input flex-1"
                        />
                        <button data-testid="neg-send" onClick={sendNeg} disabled={busy} className="nexus-btn-primary !py-2 !px-3"><Send size={14} /></button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
}
